import { Badge } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import classnames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { isMenuLinkSelected } from 'src/Components/Menu/isMenuLinkSelected';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';

import { MenuLink } from '../MenuLink';

import styles from './MenuCollapsible.module.scss';
import { Props } from './MenuCollapsible.types';

const STORAGE_PREFIX = 'menu.collapsible.';

const readStoredState = (storageKey: string): boolean => {
  try {
    return window.localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`) === 'open';
  } catch {
    return false;
  }
};

const writeStoredState = (storageKey: string, open: boolean) => {
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${storageKey}`, open ? 'open' : 'closed');
  } catch {
    // storage unavailable — ignore
  }
};

const MenuCollapsible = ({ label, storageKey, items, onItemClick }: Props) => {
  const [open, setOpen] = useState(() => readStoredState(storageKey));
  const navigate = useNavigate();
  const location = useLocation();
  const assetId = useCurrentAssetId();

  useEffect(() => {
    writeStoredState(storageKey, open);
  }, [open, storageKey]);

  const toggle = useCallback(() => setOpen(prev => !prev), []);

  const isSelected = (itemLink?: string): boolean =>
    isMenuLinkSelected(location.pathname, itemLink);

  return (
    <li className={styles.container}>
      <div
        className={styles.header}
        onClick={toggle}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggle();
          }
        }}
      >
        <IconChevronRight
          height={16}
          className={classnames(styles.chevron, { [styles.chevronOpen]: open })}
        />
        <span className={styles.label}>{label}</span>
        <Badge size="xs" radius="sm" className={styles.pastille}>
          {items.length}
        </Badge>
      </div>
      {open && (
        <ul className={styles.items}>
          {items.map((item, index) => {
            const link =
              item.getLink?.() ?? (assetId ? item.getLinkWithAssetId?.(assetId) : undefined);
            return (
              <MenuLink
                key={[item.label, index].join('-')}
                label={item.label}
                icon={item.icon}
                selected={isSelected(link)}
                onClick={() => {
                  if (link) navigate(link);
                  onItemClick();
                }}
              />
            );
          })}
        </ul>
      )}
    </li>
  );
};

export default MenuCollapsible;
